import { useCallback, useMemo } from 'react';

import { usePromptSuggestions } from '@atlassian/conversation-assistant/controllers/prompt-suggestions';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { usePreloadSmartCreationModal } from '@confluence/smart-create/entry-points/usePreloadSmartCreationModal.ts';

export type UseSmartCreatePreloadOptions = {
	isDisabled?: boolean;
	includePromptSuggestions?: boolean;
};

export function useSmartCreatePreload({
	isDisabled = false,
	includePromptSuggestions = true,
}: UseSmartCreatePreloadOptions = {}): { preload: () => void } {
	const preloadSmartCreationModal = usePreloadSmartCreationModal();

	const { loadSuggestions } = useOptionalPromptSuggestions(includePromptSuggestions);

	const preload = useCallback(() => {
		preloadSmartCreationModal();

		if (!includePromptSuggestions) {
			return;
		}

		if (
			// FG preconditioning deliberate - proper exp exposures more important
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			!isDisabled &&
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning, confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			fg('confluence_personalized_prompts_header_preload') &&
			expValEquals('confluence_personalized_prompt_suggestions', 'isEnabled', true)
		) {
			void loadSuggestions?.();
		}
	}, [includePromptSuggestions, isDisabled, preloadSmartCreationModal, loadSuggestions]);

	return { preload };
}

function useOptionalPromptSuggestions(includePromptSuggestions: boolean): {
	loadSuggestions: (() => unknown) | undefined;
} {
	const { cloudId } = useSessionData();
	const promptSuggestionsParams = useMemo(
		() => ({
			assistanceServiceParams: {
				product: 'confluence',
				experienceId: 'confluence-smart-create',
				touchpointSource: 'smart-create-preload-prompt-suggestions',
				siteId: cloudId,
			},
		}),
		[cloudId],
	);

	const { loadSuggestions } = usePromptSuggestions(promptSuggestionsParams);
	return { loadSuggestions: includePromptSuggestions ? loadSuggestions : undefined };
}
